import { useState } from 'react';
import {
  PLAN_LOOKUP_KEY_MAP, PlanLimit,
  planLimitsLabels,
  PriceLookupKey,
  SubscriptionInputValues,
  SubscriptionPlan
} from '@next-is-free-monorepo/next-is-free-types';
import { Modal } from 'antd';
import SubscriptionPlanSelector from './SubscriptionPlanSelector';
import useShopAttrs from '../../utils/hooks/useShopAttrs';
import { $api } from '../../utils/openApiClient';
import Loader from '../Loader';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  AlertIcon,
  CheckmarkBadge01Icon, Discount01FreeIcons, DiscountFreeIcons,
  MessageQuestionFreeIcons,
  QuestionFreeIcons
} from '@hugeicons/core-free-icons';
import { NBgIcon, NButton, NModal, NModalProps } from '@next-is-free-monorepo/nif-web-components';
import PromoCodeInput from './PromoCodeInput';
import AddPaymentMethod from './AddPaymentMethod';
import { supabase } from '../../utils/supabase';
import { wait } from '@testing-library/react-native/build/user-event/utils';

interface ChoosePlanModalProps {
  type?: 'promo_code' | 'default';
  enableAutoRenewal?: boolean;
  open: boolean;
  onClose: () => void;
  enableAllPlans?: boolean;
}

const ChoosePlanModal = ({
                           type = 'default',
                           enableAutoRenewal,
                           open,
                           onClose,
                           enableAllPlans
                         }: ChoosePlanModalProps) => {

  const [modalScreen, setModalScreen] = useState<undefined | 'exceeded_limits' | 'error' | 'add_payment_method' | 'confirm' | 'success' | 'loading'>(undefined);
  const [requestBody, setRequestBody] = useState<undefined | SubscriptionInputValues>(undefined);

  const [exceededLimitsModal, setExceededLimitsModal] = useState<{
    limit: PlanLimit
    used: number
    max: number
  }[]>([]);


  const shopAttrs = useShopAttrs();
  const paymentMethods = $api.useSuspenseQuery('get', '/shop/payment-methods');

  const disabledPrices: PriceLookupKey[] = [];
  if (shopAttrs.data.isSubscriptionValid && !enableAllPlans) {
    disabledPrices.push(shopAttrs.data.subscription_price_lookup_key);
  }

  const checkPlanLimits = $api.useMutation('post', '/shop/subscription/check-plan-limits', {
    onSuccess: (data, variables) => {
      if (data.length > 0) {
        setExceededLimitsModal(data as { limit: PlanLimit, used: number, max: number }[]);
        setModalScreen('exceeded_limits');
      } else {
        if (paymentMethods.data.some(pm => pm.is_default)) {
          setModalScreen('confirm');
        } else {
          setModalScreen('add_payment_method');
        }
      }
    }
  });

  function onSelectPlan(priceLookupKey: PriceLookupKey, plan: SubscriptionPlan, promoCode?: string) {
    setRequestBody({ priceLookupKey, promoCode, enableAutoRenewal });
    setModalScreen('loading');
    checkPlanLimits.mutate({ body: { plan: plan } });
  }

  const updateSubscription = $api.useMutation('put', '/shop/subscription', {
    onSuccess: () => {
      setModalScreen('success');
    },
    onError: () => {
      setModalScreen('error');
    }
  });
  const createSubscription = $api.useMutation('post', '/shop/subscription', {
    onSuccess: () => {
      setModalScreen('success');
    },
    onError: () => {
      setModalScreen('error');
    }
  });

  function onConfirm() {
    setModalScreen('loading');
    if (!requestBody) {
      return setModalScreen(undefined);
    }
    if (shopAttrs.data.isSubscriptionValid) {
      updateSubscription.mutate({ body: { ...requestBody, enableAutoRenewal } });
    } else {
      createSubscription.mutate({ body: { ...requestBody, enableAutoRenewal } });
    }
  }

  const modalScreens: Record<string, NModalProps> = {
    loading: {
      open: true,
      hideIcon: true,
      customFooter: <div />,
      children: (<div className={'flex justify-center items-center h-30'}>
        <Loader />
      </div>),
      onClose: () => {
      }
    },
    confirm: {
      open: true,
      onClose: () => setModalScreen(undefined),
      title: 'Conferma',
      text: 'Stai per attivare il piano selezionato. Conferma per iniziare subito.',
      confirmButtonText: 'Conferma',
      onConfirm: () => onConfirm(),
      hugeIcon: MessageQuestionFreeIcons,
      color: 'blue'
    },
    exceeded_limits: {
      open: true,
      onClose: () => setModalScreen(undefined),
      title: 'Hai superato i limiti di utilizzo per questo piano:',
      children: (exceededLimitsModal.map((limit) => {
        return (
          <div className={'flex items-center gap-2 mt-4'} key={limit.limit}>
            <HugeiconsIcon icon={AlertIcon} />
            <p className={'font-bold'}>{planLimitsLabels[limit.limit]}</p>
            <p className={'text-sm text-slate-500'}>{limit.used}/{limit.max}</p>
          </div>
        );
      })),
      hideConfirmButton: true,
      cancelButtonText: 'Ok',
      hugeIcon: AlertIcon,
      color: 'red'
    },
    add_payment_method: {
      open: true,
      onClose: () => setModalScreen(undefined),
      hideIcon: true,
      width: 420,
      customFooter: <div />,
      children: (
        <div className={'flex justify-center items-center'}>
          <AddPaymentMethod onSuccess={() => setModalScreen('confirm')} />
        </div>
      )
    },
    error: {
      open: true,
      onClose: () => setModalScreen(undefined),
      title: 'Ops! Qualcosa è andato storto, riprova',
      hideConfirmButton: true,
      cancelButtonText: 'Ok',
      hugeIcon: AlertIcon,
      color: 'red'
    },
    success: {
      open: true,
      onClose: () => {
        setModalScreen('loading');
        setTimeout(async () => {
          //this is needed to give time to the webhook
          await supabase.auth.refreshSession();
          window.location.reload();
        }, 3000);
        window.location.reload();
      },
      onConfirm: () => {
        setModalScreen('loading');
        setTimeout(async () => {
          //this is needed to give time to the webhook
          await supabase.auth.refreshSession();
          window.location.reload();
        }, 3000);
      },
      confirmButtonText: 'Ok',
      color: 'green',
      hugeIcon: CheckmarkBadge01Icon,
      title: 'Abbonamento aggiornato',
      text: 'Il tuo abbonamento è stato aggiornato con successo.',
      hideCancelButton: true
    }
  };

  return (
    <div>
      <Modal open={open && type === 'default'} onCancel={onClose} footer={null} style={{ top: 10 }}
             width={'100vw'}>
        <SubscriptionPlanSelector disabledPrices={disabledPrices}
                                  onSelect={(priceLookupKey, plan) => onSelectPlan(priceLookupKey, plan)} />
      </Modal>
      <NModal hugeIcon={DiscountFreeIcons} color={'blue'} title={'Inserisci un codice sconto'} customFooter={<div />}
              open={open && type === 'promo_code'} onClose={onClose}>
        <PromoCodeInput
          onConfirm={(promoPlan) => onSelectPlan(promoPlan.price_lookup_key, PLAN_LOOKUP_KEY_MAP[promoPlan.price_lookup_key], promoPlan.code)} />
      </NModal>
      {modalScreen &&
        <NModal {...modalScreens[modalScreen]} />
      }
    </div>
  );
};

export default ChoosePlanModal;
